<template>
  <ListView
    :isFluid="true"
    :title="title"
    @reload="reload"
    @navDetails="handleItemClick"
    :search.sync="search"
    :data="data"
    :page.sync="page"
    :header="header"
    :hideCreateButton="true"
    :search-box-placeholder="searchBoxPlaceholder"
  >
    <template v-slot:filter>
      <div class="d-flex align-center mb-8 flex-gap">
        <v-btn
          v-if="isClinicStaff"
          color="success"
          :loading="markingAllAsRead"
          @click="markAllAsRead"
        >
          <v-icon left>mdi-check-all</v-icon>
          Mark All as Read
        </v-btn>
      </div>
    </template>
    <template v-slot:item.content="{ item }">
      <div class="text-truncate" style="max-width: 400px">
        {{ item.content | parseAndLocalize('en') }}
      </div>
    </template>
    <template v-slot:item.read="{ item }">
      <v-chip v-if="item.read" small label color="success">Read</v-chip>
      <v-chip v-else small label color="warning">Unread</v-chip>
    </template>
    <template v-slot:item.created_at="{ item }">
      {{ item.created_at | moment('DD-MMM-YYYY HH:mm') }}
    </template>
    <template v-slot:item.profile="{ item }">
      <div v-if="item.profile">{{ item.profile.name | parseAndLocalize('en') }}</div>
      <div v-if="item.profile">{{ item.profile.phone }}</div>
    </template>

    <NotificationDetailDialog
      v-model="detailDialog"
      :notification="selectedNotification"
      @read="handleNotificationRead"
    />
  </ListView>
</template>

<script>
import {
  listNotification,
  listStaffNotification,
  notificationMarkAsRead,
} from '@/api/notification';
import listViewPageMixin from '@/services/listViewPageMixin';
import { mapGetters, mapActions } from 'vuex';
import {
  reCheckIfHasUnreadNotification,
  syncClinicUnreadNotificationCount,
} from '@/plugins/app-state';
import { getId } from '@/services/filters';
import NotificationDetailDialog from './NotificationDetailDialog.vue';
import api from '@/api';

export default {
  name: 'NotificationList',
  data() {
    return {
      header: [],
      populate: [{ path: 'profile', select: ['name', 'phone'] }],
      detailDialog: false,
      selectedNotification: null,
      markingAllAsRead: false,
      searchBoxPlaceholder: 'Search by Subject/ Content',
    };
  },
  mixins: [listViewPageMixin],
  components: { NotificationDetailDialog },
  computed: {
    ...mapGetters('auth', [
      'role',
      'isAdmin',
      'isClinicStaff',
      'isCashier',
      'profile',
      'profileId',
      'clinicId',
    ]),
    title() {
      return 'Notifications';
    },
    restfulURL() {
      return 'rml/admin/Notifications';
    },
    filter() {
      // For clinic staff, filter notifications by their profile OR their clinic profile
      if (this.isClinicStaff && (this.profileId || this.clinicId)) {
        const profiles = [];
        if (this.profileId) profiles.push(this.profileId);
        if (this.clinicId) profiles.push(this.clinicId);

        return {
          profile: { $in: profiles },
        };
      }
      return null;
    },
  },
  mounted() {
    this.setupHeaders();
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    setupHeaders() {
      this.header = [
        { text: 'Date', value: 'created_at' },
        { text: 'Recipient', value: 'profile' },
        { text: 'Content', value: 'content' },
        { text: 'Status', value: 'read' },
        { text: '', value: 'operation' },
      ];
    },

    async markAllAsRead() {
      const confirmed = await this.$root.$confirm.open(
        'Confirm Mark All as Read',
        'Are you sure you want to mark all notifications as read?',
        { color: 'success' },
      );

      if (!confirmed) return;

      try {
        this.markingAllAsRead = true;
        await api.patch('rml/me/Notification/readAll');

        await this.updateAlertMessage({
          msg: 'All notifications marked as read',
          type: 'success',
          color: 'success',
        });

        // Update notification unread count
        await syncClinicUnreadNotificationCount(this.$store, api);

        // Reload the list to show updated read status
        await this.reload();
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
        await this.updateAlertMessage({
          msg: 'Failed to mark all notifications as read',
          type: 'error',
        });
      } finally {
        this.markingAllAsRead = false;
      }
    },

    async handleNotificationRead(notificationId) {
      // Update notification unread count
      await syncClinicUnreadNotificationCount(this.$store, api);

      // Reload the list after a notification is marked as read
      this.reload();
    },

    handleItemClick(item) {
      this.selectedNotification = item;
      this.detailDialog = true;
    },
    formatType(type) {
      if (!type) return 'Unknown';

      return type
        .replace(/_/g, ' ')
        .replace(/:/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
  },
};
</script>

<style scoped>
.statusChip {
  min-width: 80px;
  justify-content: center;
}
</style>
